import React from 'react';
import { ButtonProps } from '@/shared/types/button';
import { InnerButton } from '@/modules/InnerButton';
import { AnyIconDefinition } from '@/shared/types/types';

/**
 * Button - Componente de botón versátil y configurable
 *
 * Componente wrapper que proporciona una API simplificada sobre InnerButton.
 * Soporta múltiples temas, colores, tamaños, iconos, badges y tooltips.
 *
 * @example
 * ```tsx
 * // Botón simple
 * <Button onClick={() => console.log('clicked')}>
 *   Hacer clic
 * </Button>
 *
 * // Botón con icono
 * <Button icon="ImageOutlined" onClick={handleClick}>
 *   Subir imagen
 * </Button>
 *
 * // Botón como enlace
 * <Button link="/dashboard" target="_blank">
 *   Ir al Dashboard
 * </Button>
 * ```
 *
 * @see {@link InnerButton} para la implementación base
 * @see {@link ButtonProps} para la lista completa de props
 */
export const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement | HTMLDivElement,
  ButtonProps
>(function Button(
  {
    className,
    fullWidth = false,
    leftSlot,
    rightSlot,
    theme = 'solid',
    badgeColor,
    badgeLabel,
    badgeTheme,
    badgeSize,
    badgeStroke,
    badgeIcon,
    badgeIconDuotonePrimary,
    badgeIconDuotoneSecondary,
    badgeIconDuotoneOpacityPrimary,
    badgeIconDuotoneOpacitySecondary,
    clickable = true,
    color = 'primary',
    rounded,
    children,
    icon,
    link,
    size = 'sm',
    target = '_self',
    onClickIcon,
    disabled,
    colorIcon,
    tooltip,
    tooltipPosition,
    tooltipAlignment,
    tooltipDelay,
    iconProps: iconPropsUser,
    ...props
  }: ButtonProps,
  ref: React.ForwardedRef<HTMLButtonElement | HTMLAnchorElement | HTMLDivElement>,
) {
  const iconPropsFromLegacy =
    iconPropsUser == null &&
    icon != null &&
    !React.isValidElement(icon) &&
    (colorIcon != null || onClickIcon != null)
      ? {
        icon: icon as AnyIconDefinition,
        show: true as const,
        ...(colorIcon != null ? { color: colorIcon } : {}),
        ...(onClickIcon != null ? { onClick: onClickIcon } : {}),
      }
      : undefined;

  const resolvedIconProps = iconPropsUser ?? iconPropsFromLegacy;
  const iconForInner = resolvedIconProps != null && !React.isValidElement(icon) ? undefined : icon;
  const colorIconForInner = resolvedIconProps != null ? undefined : colorIcon;
  const onClickIconForInner = resolvedIconProps != null ? undefined : onClickIcon;

  return (
    <InnerButton
      ref={ref}
      theme={theme}
      color={color}
      rounded={rounded}
      fullWidth={fullWidth}
      size={size}
      clickable={clickable}
      children={children}
      icon={iconForInner}
      iconProps={resolvedIconProps}
      badgeColor={badgeColor}
      badgeLabel={badgeLabel}
      badgeTheme={badgeTheme}
      badgeSize={badgeSize}
      badgeStroke={badgeStroke}
      badgeIcon={badgeIcon}
      badgeIconDuotonePrimary={badgeIconDuotonePrimary}
      badgeIconDuotoneSecondary={badgeIconDuotoneSecondary}
      badgeIconDuotoneOpacityPrimary={badgeIconDuotoneOpacityPrimary}
      badgeIconDuotoneOpacitySecondary={badgeIconDuotoneOpacitySecondary}
      leftSlot={leftSlot}
      rightSlot={rightSlot}
      link={link}
      target={target}
      onClickIcon={onClickIconForInner}
      disabled={disabled}
      colorIcon={colorIconForInner}
      className={className}
      tooltip={tooltip}
      tooltipPosition={tooltipPosition}
      tooltipAlignment={tooltipAlignment}
      tooltipDelay={tooltipDelay}
      {...props}
    />
  );
});

Button.displayName = 'Button';
